import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, TrendingUp, Zap, Sparkles, Brain, Calendar,
  FileText, Users, ExternalLink, Download, Search, Filter,
  BarChart3, Clock, CheckCircle, Eye, Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyActivityPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('mi');
  const [loading, setLoading] = useState(true);
  const [activityData, setActivityData] = useState({
    miReports: [],
    advancedSourcing: [],
    smartMatch: [],
    sourcingDoctor: []
  });

  useEffect(() => {
    fetchActivityData();
  }, []);

  const fetchActivityData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/activity`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setActivityData(data);
      }
    } catch (error) {
      console.error('Error fetching activity:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    {
      id: 'mi',
      name: 'MI Reports',
      icon: TrendingUp,
      color: 'from-blue-500 to-cyan-500',
      count: activityData.miReports?.length || 0
    },
    {
      id: 'advanced',
      name: 'Advanced Sourcing',
      icon: Zap,
      color: 'from-violet-500 to-purple-500',
      count: activityData.advancedSourcing?.length || 0
    },
    {
      id: 'smartmatch',
      name: 'SmartMatch',
      icon: Sparkles,
      color: 'from-purple-500 to-pink-500',
      count: activityData.smartMatch?.length || 0
    },
    {
      id: 'doctor',
      name: 'Sourcing Doctor',
      icon: Brain,
      color: 'from-orange-500 to-red-500',
      count: activityData.sourcingDoctor?.length || 0
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-purple-50">
      <div className="container mx-auto px-6 py-8 pt-24">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-xl">
              <BarChart3 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-slate-900">My Activity</h1>
              <p className="text-slate-600 mt-1">Track all your searches, reports, and insights</p>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <Card key={tab.id} className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-600 mb-1">{tab.name}</p>
                        <p className="text-2xl font-bold text-slate-900">{tab.count}</p>
                      </div>
                      <div className={`w-12 h-12 bg-gradient-to-br ${tab.color} rounded-xl flex items-center justify-center`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
          {/* Tab Headers */}
          <div className="border-b border-slate-200 bg-slate-50">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-4 font-medium transition-all ${
                      activeTab === tab.id
                        ? 'text-violet-600 border-b-2 border-violet-600 bg-white'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {tab.name}
                    <Badge variant="secondary" className="ml-2">{tab.count}</Badge>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="w-12 h-12 text-violet-500 animate-spin mb-4" />
                <p className="text-slate-600">Loading your activity...</p>
              </div>
            ) : (
              <>
                {activeTab === 'mi' && <MIReportsTab data={activityData.miReports} />}
                {activeTab === 'advanced' && <AdvancedSourcingTab data={activityData.advancedSourcing} />}
                {activeTab === 'smartmatch' && <SmartMatchTab data={activityData.smartMatch} />}
                {activeTab === 'doctor' && <SourcingDoctorTab data={activityData.sourcingDoctor} />}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// MI Reports Tab Component
const MIReportsTab = ({ data }) => {
  const navigate = useNavigate();

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-16">
        <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-700 mb-2">No MI Reports Yet</h3>
        <p className="text-slate-500 mb-6">Start generating market intelligence reports to see them here</p>
        <Button
          onClick={() => navigate('/market-intelligence')}
          className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
        >
          Generate MI Report
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {data.map((report, index) => (
        <Card key={index} className="border border-slate-200 hover:border-violet-300 hover:shadow-lg transition-all">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{report.job_title}</h3>
                    <p className="text-sm text-slate-600">{report.job_location} • {report.work_type}</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  <Badge variant="secondary" className="text-xs">
                    <Calendar className="w-3 h-3 mr-1" />
                    {new Date(report.created_at).toLocaleDateString()}
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    <Clock className="w-3 h-3 mr-1" />
                    {new Date(report.created_at).toLocaleTimeString()}
                  </Badge>
                  {report.mi_report && (
                    <Badge className="bg-green-100 text-green-700 text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Complete
                    </Badge>
                  )}
                </div>

                {report.mi_report?.salary_analysis && (
                  <p className="text-sm text-slate-600">
                    <strong>Salary Range:</strong> {report.mi_report.salary_analysis.base_salary_range}
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/market-intelligence/report/${report.report_id}`)}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Advanced Sourcing Tab Component
const AdvancedSourcingTab = ({ data }) => {
  const navigate = useNavigate();

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-16">
        <Zap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-700 mb-2">No Searches Yet</h3>
        <p className="text-slate-500 mb-6">Start searching for candidates to see your history here</p>
        <Button
          onClick={() => navigate('/advanced-sourcing')}
          className="bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600"
        >
          Search Candidates
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {data.map((search, index) => (
        <Card key={index} className="border border-slate-200 hover:border-violet-300 hover:shadow-lg transition-all">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-500 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{search.query}</h3>
                    <p className="text-sm text-slate-600">{search.candidates_found || 0} candidates found</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary" className="text-xs">
                    <Calendar className="w-3 h-3 mr-1" />
                    {new Date(search.created_at).toLocaleDateString()}
                  </Badge>
                  <Badge className="bg-violet-100 text-violet-700 text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {search.candidates_found} Results
                  </Badge>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/advanced-sourcing')}
              >
                <Search className="w-4 h-4 mr-2" />
                Search Again
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// SmartMatch Tab Component
const SmartMatchTab = ({ data }) => {
  const navigate = useNavigate();

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-16">
        <Sparkles className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-700 mb-2">No SmartMatch Sessions Yet</h3>
        <p className="text-slate-500 mb-6">Start scoring candidates to see your results here</p>
        <Button
          onClick={() => navigate('/smartmatch')}
          className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
        >
          Score Candidates
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {data.map((session, index) => (
        <Card key={index} className="border border-slate-200 hover:border-violet-300 hover:shadow-lg transition-all">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{session.job_title || 'Candidate Analysis'}</h3>
                    <p className="text-sm text-slate-600">{session.candidates_scored?.length || 0} candidates scored</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  <Badge variant="secondary" className="text-xs">
                    <Calendar className="w-3 h-3 mr-1" />
                    {new Date(session.created_at).toLocaleDateString()}
                  </Badge>
                  {session.candidates_scored && session.candidates_scored.length > 0 && (
                    <Badge className="bg-purple-100 text-purple-700 text-xs">
                      Top Score: {Math.max(...session.candidates_scored.map(c => c.overall_score || 0))}/10
                    </Badge>
                  )}
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/smartmatch/results?session_id=${session.session_id}`)}
              >
                <Eye className="w-4 h-4 mr-2" />
                View Results
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Sourcing Doctor Tab Component
const SourcingDoctorTab = ({ data }) => {
  const navigate = useNavigate();

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-16">
        <Brain className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-700 mb-2">No Consultations Yet</h3>
        <p className="text-slate-500 mb-6">Get AI-powered sourcing advice from the Sourcing Doctor</p>
        <Button
          onClick={() => navigate('/ojas')}
          className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
        >
          Consult Doctor
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {data.map((session, index) => (
        <Card key={index} className="border border-slate-200 hover:border-violet-300 hover:shadow-lg transition-all">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{session.query || 'Sourcing Consultation'}</h3>
                    <p className="text-sm text-slate-600 line-clamp-2">{session.response?.substring(0, 150)}...</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary" className="text-xs">
                    <Calendar className="w-3 h-3 mr-1" />
                    {new Date(session.created_at).toLocaleDateString()}
                  </Badge>
                  <Badge className="bg-orange-100 text-orange-700 text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {session.feedback_rating ? `${session.feedback_rating}★` : 'Completed'}
                  </Badge>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/ojas')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                New Session
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default MyActivityPage;
